import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";
import { cookieUtils } from "../../../utils/cookieUtils";
import { ErrorToast } from "../../../components/toastMessage";



/**
 * unapproved user available pages routes.
 */
const WHITELIST_ROUTES = [
  "/account-verification-status",
  "/user-account-not-verified",
  "/launch-page",
  "/user-profile",
];

/**
 * Props definition for the RoleGuard component.
 */
interface RoleGuardProps {
  allowedRoles: string | string[]; // Roles that are permitted to access the component
  children: React.ReactElement; // The component to be rendered if access is granted
}

/**
 * RoleGuard component restricts access to certain pages based on user roles.
 * - If a user is not verified, they are redirected to an account verification page.
 * - If a user does not have the required role, they are redirected to the login page.
 * - Whitelisted routes are always accessible.
 *
 * @param {RoleGuardProps} props - The properties for the guard.
 * @returns {React.ReactElement} - The protected component if access is granted, otherwise a redirect.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ allowedRoles, children }) => {
  const location = useLocation();
  const user = useUser();

  // Normalize allowedRoles to always be an array
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  // Retrieve additional user details
  const userRegistrationDetails = JSON.parse(
    localStorage.getItem("userRegistrationDetails") || "{}"
  ) as Record<string, any>;

  const userDetails = cookieUtils.getSecureCookie("userDetails");

  /**
   * Restrict unverified users from accessing non-whitelisted pages.
   * - If the current route is NOT in the whitelist and the user is "user" but not verified,
   *   they are redirected to the account verification page.
   */
  
  if (
    !WHITELIST_ROUTES.includes(location.pathname) && // If the current route is NOT whitelisted
    userDetails?.role === "user" && // The user's role is "user"
    userRegistrationDetails?.regestration_status !== 1 // The user is not verified
  ) {
    ErrorToast("Access denied");
    return <Navigate to="/user-account-not-verified" replace />;
  }

  
  /**
   * Allow access to all roles if no restrictions are set.
   * - If allowedRoles is an empty array, this route is accessible to everyone.
   */
  if (roles.length === 0) {
    return children;
  }

  /**
   * Restrict access to users whose roles do not match the allowed roles.
   * - If the user's role is not found in the allowed roles list, redirect to login.
   */
  
  if (!roles.includes(user?.role?.toLowerCase() || "")) {
    return <Navigate to="/onboarding/login" replace />;
  }

  // If all checks pass, allow access to the component
  return children;
};

export default RoleGuard;
